import {Box, Button, Typography} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import dayjs from 'dayjs';
import {ChangeEvent, FC, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {transactionsApi} from 'src/api';
import {tagsApi} from 'src/api/tags';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import {ChargerTransactionCard} from 'src/components/chargers/ChargerTransactionsTable/ChargerTransactionCard';
import AMPModal from 'src/components/common/AMPModal';
import {FilterBar} from 'src/components/common/FilterBar';
import {ResponsiveTable} from 'src/components/common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {BlockStatusEnum} from 'src/components/common/RfidTable/RfidTable.types';
import {CreateRfidFormFromDedicatedPage} from 'src/components/forms/CreateRfidFormFromDedicatedPage';
import {EmptyTable} from 'src/components/tables/EmptyTable';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {
    AutorenewOutlined,
    BoltOutlined,
    CreditCardOutlined,
    DeleteOutlineOutlined,
    DoDisturbAltOutlined,
    Edit,
} from '@mui/icons-material';
import {
    blockButtonSx,
    deleteButtonSx,
    editButtonSx,
    factLabelSx,
    factSx,
    factValueSx,
    identityCardSx,
    identityFactsSx,
    identityHeaderSx,
    ownerLinkSx,
    ownerTypeChipSx,
    pageSx,
    statusPillSx,
    tagBadgeSx,
    tagSubtitleSx,
    tagTitleSx,
} from './RfidPage.sx';
import {columnConfigTransactions} from './RfidPage.init';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {tagOwnerLabelId, tagOwnerOf} from 'src/utils/tagOwner';
import {NotifyError} from 'src/utils/notification';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';

export const RfidPage: FC = () => {
    const persona = usePersona();
    const intl = useIntl();

    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const title = intl.formatMessage({id: 'transactions'});

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const isCompanyUser = persona.isCompany;
    const isEmployeeUser = persona.isEmployee;
    // Each detail page asks about its own entity; `singlePages` was one subject for all of
    // them and, being absent from the catalog, sent every one of them straight to /404.

    const {id} = useParams();
    const tagId = Number(id);

    const [showAddRfidModal, setShowAddRfidModal] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [perPage, setPerPage] = useState<number>(100);

    const [toggleTagTrigger] = tagsApi.useToggleTagStatusMutation();
    // Three personas were listed for all three buttons, and the three buttons are not one action:
    // editing and blocking a card go through rfidTags.update, destroying it through rfidTags.delete.
    const canUpdateTag = useCan('rfidTags.update');
    const canDeleteTag = useCan('rfidTags.delete');
    const [deleteTagTrigger] = tagsApi.useDeleteTagMutation();

    // Fetch a single tag by numeric ID
    const {data: currentTag, refetch: refetchTag} = tagsApi.useGetTagByIdQuery({
        tagId,
        params: {
            businessId: businessId ?? 0,
            companyId: isEmployeeUser ? authInfo?.employee?.company?.id : isCompanyUser ? authInfo?.company?.id : undefined,
        },
    });

    // Fetch transactions with proper pagination
    const {data: transactionsData} = transactionsApi.useGetTransactionsQuery(
        {
            data: {
                businessId: businessId,
                tagId: currentTag?.id,
            },
            params: {
                page: currentPage,
                size: perPage,
            },
        },
        {
            skip: !currentTag?.id,
        }
    );

    const handleChangePage = (event: number) => {
        setCurrentPage(event);
    };

    const handleShowAddRfidModal = (isActive: boolean) => () => {
        setShowAddRfidModal(isActive);
    };

    const editTagHandler = () => {
        handleShowAddRfidModal(true)();
    };

    const triggerTagHandler = async () => {
        const tagId = currentTag?.id ?? 0;

        try {
            dispatch(preloaderChangeStatus(true));

            await toggleTagTrigger({
                tagId,
                params: {
                    businessId: businessId,
                    blocked: !currentTag?.blocked,
                },
            });

            refetchTag();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const deleteTagHandler = async () => {
        const tagId = currentTag?.id ?? 0;

        try {
            dispatch(preloaderChangeStatus(true));

            await deleteTagTrigger({
                tagId,
                params: {
                    businessId: businessId,
                },
            }).unwrap();

            navigate('/app/rfids');
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleRedirection = (newId: string) => {
        navigate(`/app/rfids/${newId}`);
    };

    const handlePerPageChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const val = +event.target.value;
        if (typeof val === 'number') {
            setPerPage(val);
            setCurrentPage(0);
        }
    };

    // Who holds the card and what kind of account they are - the list's own reading, so this page no
    // longer calls a manager a private driver, or a company login "null null".
    const cardOwner = useMemo(() => tagOwnerOf(currentTag), [currentTag]);
    const tagLabel = cardOwner?.type ? tagOwnerLabelId(cardOwner.type) : null;

    /**
     * Where the owner's own page is, when there is one.
     *
     * The list has made this name a link for a long time; on the card's own page it was plain text,
     * so the one step a reader wants from here - who holds this card - was the one they had to take
     * by going back to the list and finding the row again.
     */
    const ownerLink = useMemo(() => {
        if (currentTag?.employee?.id) {
            // An employee's page lives under users and needs both ids as query params.
            //
            // The company is read where it actually is. `employee.companyId` is not filled in this
            // payload, and the 0 it fell back to produced a real page with a failing request behind
            // it - "Company 0 for business 1 not found" - while the same person opened from the users
            // list, which reads `employee.company.id`, worked.
            const employeeBusinessId = currentTag.business?.id ?? businessId;
            // The employee's own company first: we are linking to that person's page, and a tag can
            // also carry a company of its own, which for an employee's tag is not necessarily theirs.
            const employeeCompanyId = currentTag.employee.company?.id
                ?? currentTag.employee.companyId
                ?? currentTag.company?.id;

            // No company, no link: an address that is certain to fail is worse than plain text.
            if (!employeeBusinessId || !employeeCompanyId) {
                return null;
            }

            return `${AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, 'employee', String(currentTag.employee.id)])}`
                + `?businessId=${employeeBusinessId}&companyId=${employeeCompanyId}`;
        }

        return cardOwner?.userId
            ? AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(cardOwner.userId)])
            : null;
    }, [currentTag, cardOwner, businessId]);

    const ownerLabel = cardOwner?.name || null;

    if (!currentTag) {
        return null;
    }

    return (
        <Box sx={pageSx}>
            {/* The card itself: what it is, what may be done to it, and the four facts about it. It
                used to be a narrow column beside a nine-row table, so the facts ran out a third of
                the way down and left the rest of the column empty. */}
            <Box sx={identityCardSx}>
                <Box sx={identityHeaderSx}>
                    <Box sx={tagBadgeSx}>
                        <CreditCardOutlined/>
                    </Box>
                    <Box sx={{minWidth: 0}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'}}>
                            <Typography sx={tagTitleSx}>{currentTag?.idTag ?? ''}</Typography>
                            <Box component="span" sx={statusPillSx(Boolean(currentTag?.blocked))}>
                                <FormattedMessage
                                    id={currentTag?.blocked ? BlockStatusEnum.blocked : BlockStatusEnum.accepted}
                                />
                            </Box>
                        </Box>
                        <Typography sx={tagSubtitleSx}>
                            <FormattedMessage id="rfidTagAddedOnTableHeader"/>
                            {' '}
                            {dayjs(currentTag.created).format('DD/MM/YYYY')}
                        </Typography>
                    </Box>

                    <Box sx={{flex: '1 1 auto'}}/>

                    {/* Same two permissions as before: editing and blocking are one right,
                        destroying the card is another. */}
                    {canUpdateTag && (
                        <Button
                            variant="contained"
                            onClick={editTagHandler}
                            sx={editButtonSx}
                            startIcon={<Edit/>}
                        >
                            <FormattedMessage id="edit"/>
                        </Button>
                    )}

                    {canUpdateTag && (
                        <Button
                            variant="contained"
                            onClick={triggerTagHandler}
                            sx={blockButtonSx}
                            startIcon={currentTag.blocked ? <AutorenewOutlined/> : <DoDisturbAltOutlined/>}
                        >
                            <FormattedMessage id={currentTag.blocked ? 'unblockRfidTag' : 'blockRfidTag'}/>
                        </Button>
                    )}

                    {canDeleteTag && (
                        <Button
                            variant="contained"
                            onClick={deleteTagHandler}
                            sx={deleteButtonSx}
                            startIcon={<DeleteOutlineOutlined/>}
                        >
                            <FormattedMessage id="delete"/>
                        </Button>
                    )}
                </Box>

                <Box sx={identityFactsSx}>
                    <Box sx={factSx}>
                        <Typography sx={factLabelSx}>
                            <FormattedMessage id="owner"/>
                        </Typography>
                        {ownerLabel && ownerLink ? (
                            <Typography component={Link} to={ownerLink} sx={ownerLinkSx}>
                                {ownerLabel}
                            </Typography>
                        ) : (
                            <Typography sx={factValueSx}>{ownerLabel ?? '—'}</Typography>
                        )}
                    </Box>

                    <Box sx={factSx}>
                        <Typography sx={factLabelSx}>
                            <FormattedMessage id="clientsRoleTableHeader"/>
                        </Typography>
                        {tagLabel ? (
                            <Box component="span" sx={ownerTypeChipSx}>
                                <FormattedMessage id={tagLabel as string}/>
                            </Box>
                        ) : (
                            <Typography sx={factValueSx}>—</Typography>
                        )}
                    </Box>

                    <Box sx={factSx}>
                        <Typography sx={factLabelSx}>
                            <FormattedMessage id="licensePlate"/>
                        </Typography>
                        <Typography sx={factValueSx}>{currentTag.licensePlate || '—'}</Typography>
                    </Box>

                    <Box sx={factSx}>
                        <Typography sx={factLabelSx}>
                            <FormattedMessage id="rfidTagAddedOnTableHeader"/>
                        </Typography>
                        <Typography sx={factValueSx}>
                            {dayjs(currentTag.created).format('DD/MM/YYYY')}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <TableLayout
                toolbar={(
                    <FilterBar
                        title={title}
                        icon={<BoltOutlined/>}
                        subtitle={intl.formatMessage(
                            {id: 'rowsFound'},
                            {count: transactionsData?.page?.totalElements ?? 0}
                        )}
                    />
                )}
                totalPages={transactionsData?.page?.totalPages ?? 0}
                page={currentPage}
                pageSize={perPage}
                onPageChange={handleChangePage}
                onPageSizeChange={handlePerPageChange}
            >
                <ResponsiveTable
                    desktopElement={
                        <Table
                            sx={{
                                border: 'none',
                            }}
                            rows={transactionsData?.content ?? []}
                            hideFooter
                            columns={columnConfigTransactions}
                        />
                    }
                    responsiveElement={
                        <>
                            <CardList>
                                {transactionsData?.content?.map((item) => (
                                    <ChargerTransactionCard
                                        key={item?.id}
                                        {...item}
                                    />
                                ))}
                            </CardList>
                            {!transactionsData?.content?.length && <EmptyTable/>}
                        </>
                    }
                />
            </TableLayout>

            <AMPModal
                handleClose={handleShowAddRfidModal(false)}
                open={showAddRfidModal}
                title={intl.formatMessage({id: 'editRfid'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <CreateRfidFormFromDedicatedPage
                    initialValues={{
                        tagId: currentTag?.id ?? 0,
                        idTag: currentTag?.idTag ?? '',
                        licensePlate: currentTag?.licensePlate ?? '',
                        companyId: currentTag?.company?.id ?? 0,
                        drivers: currentTag?.users ?? [],
                        employeeId: currentTag?.employee?.id ?? 0,
                        blocked: currentTag?.blocked ?? false,
                        businessId: currentTag?.business?.id ?? 0,
                    }}
                    handleClose={handleShowAddRfidModal(false)}
                    additionalSubmitAction={handleRedirection}
                />
            </AMPModal>
        </Box>
    );
};
